/**
 * Who answers for an agent — the one shape, and the one place it is checked.
 *
 * <h3>Why this is its own module</h3>
 *
 * The constant lived on a v2 component (`AccountabilitySection.jsx`), which was
 * fine while only that component and its wizard used it. A third consumer —
 * the Smart Agent Wizard's provisioning service — would have had a service
 * importing a React component for one object, or, worse, keeping its own copy
 * of the same three fields. One owner per concern; the component now reads it
 * from here and re-exports it so existing imports are untouched.
 *
 * <h3>What the backend does with each mode</h3>
 *
 * `OwnershipIntent` takes two (`OwnershipIntent.java:19-20`):
 *
 *   self     — the creator is the owner; `accepted` true attests inline, and the
 *              record goes DISCOVERED_UNOWNED → NOMINATED → ACTIVE in the create
 *              transaction (`OwnershipRepresentationService:93-103`).
 *   nominate — the record is claimed in the named owner's name and sits at
 *              NOMINATED until they attest for themselves.
 *
 * There is no third mode, and nominating nobody is not one. With a null
 * principal the backend returns early and leaves the record DISCOVERED_UNOWNED
 * (`OwnershipRepresentationService:96-98`) — so a surface that sent it would go
 * on to report a nomination the record does not contain. It is refused here
 * instead, where the person can still fix it.
 */

export const OWNER_SELF = 'self';
export const OWNER_NOMINATE = 'nominate';

/** The creator owns it and accepts inline — what an unspecified choice means. */
export const DEFAULT_ACCOUNTABILITY = Object.freeze({
  ownerMode: OWNER_SELF,
  ownerPrincipalId: null,
  accepted: true,
});

/**
 * Fill in an accountability choice and reject one that cannot mean what it says.
 *
 * @param  {Object} intent partial `{ ownerMode, ownerPrincipalId, accepted }`, or null
 * @return {Object} the complete intent to send
 * @throws {Error}  when a nomination names nobody
 */
export function normalizeAccountability(intent) {
  const a = { ...DEFAULT_ACCOUNTABILITY, ...(intent || {}) };
  if (a.ownerMode === OWNER_NOMINATE) {
    if (!a.ownerPrincipalId) {
      throw new Error('Choose who answers for this agent, or take it on yourself.');
    }
    // Only the nominated person can accept on their own behalf.
    return { ownerMode: OWNER_NOMINATE, ownerPrincipalId: a.ownerPrincipalId, accepted: false };
  }
  return { ownerMode: OWNER_SELF, ownerPrincipalId: null, accepted: a.accepted !== false };
}

export default normalizeAccountability;
